import React, { useCallback, useRef, useState } from 'react';
import SpellTextarea from '../spellcheck/SpellTextarea';
import { Send, Smile } from 'lucide-react';
import EmojiPicker from './EmojiPicker';
import './ChatMessageInput.css';

// Декодирует HTML entities (&nbsp;, &amp; и т.п.) — на случай paste из HTML-источника.
// textarea.innerHTML НЕ выполняет HTML, безопасно.
function decodeHtmlEntities(str) {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

export default function ChatMessageInput({ placeholder = 'Введите сообщение...', onSend, onTyping, disabled }) {
  const [value, setValue] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const taRef = useRef(null);
  const emojiBtnRef = useRef(null);

  const send = useCallback(() => {
    const decoded = decodeHtmlEntities(value).replace(/ /g, ' ');
    const trimmed = decoded.trim();
    if (!trimmed) return;
    onSend?.(trimmed);
    setValue('');
    // focus() не нужен: mousedown preventDefault на кнопке отправки
    // сохраняет фокус textarea. Принудительный focus на iOS дёргает клавиатуру.
  }, [value, onSend]);

  const handleChange = useCallback((newValue) => {
    setValue(newValue);
    onTyping?.();
  }, [onTyping]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const insertEmoji = useCallback((emoji) => {
    const ta = taRef.current?.element;
    const start = ta?.selectionStart ?? value.length;
    const end = ta?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    setValue(next);
    onTyping?.();
    // Не вызываем focus() — mousedown preventDefault сохраняет фокус textarea,
    // если он был. Принудительный focus на мобилках дёргает виртуальную клавиатуру.
    requestAnimationFrame(() => {
      const el = taRef.current?.element;
      if (!el) return;
      const pos = start + emoji.length;
      try { el.setSelectionRange(pos, pos); } catch (_) {}
    });
  }, [value, onTyping]);

  return (
    <div className="chat-message-input">
      <div className="chat-message-input__emoji-wrap">
        <button
          ref={emojiBtnRef}
          type="button"
          className="chat-message-input__emoji"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setPickerOpen((v) => !v)}
          disabled={disabled}
          aria-label="Эмодзи"
          aria-expanded={pickerOpen}
        >
          <Smile size={20} />
        </button>
        {pickerOpen && (
          <EmojiPicker
            onSelect={(e) => { insertEmoji(e); }}
            onClose={() => setPickerOpen(false)}
            anchorRef={emojiBtnRef}
          />
        )}
      </div>
      <SpellTextarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1}
        autoResize
        disabled={disabled}
        className="chat-message-input__field"
      />
      <button
        type="button"
        className="chat-message-input__send"
        onMouseDown={(e) => e.preventDefault()}
        onClick={send}
        disabled={disabled || !value.trim()}
        aria-label="Отправить"
      >
        <Send size={18} />
      </button>
    </div>
  );
}
